import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ReflectionTextContent from '../components/ReflectionTextContent';
import RenderMonitor from '../components/RenderMonitor';
import { supabase } from '../utils/supabase';
import { getAdminViewMode, setAdminViewMode } from '../utils/adminViewMode';
import { fetchLessonCompletionsForBootcamps } from '../utils/adminAnalyticsKpis';

const DAY_MS = 24 * 60 * 60 * 1000;
const OVERVIEW_CACHE_TTL_MS = 10 * 60 * 1000;

function parseStutterDays(stutterTime) {
  const match = String(stutterTime || '').match(/(\d+(\.\d+)?)/);
  const parsed = match ? Number(match[1]) : 1;
  return parsed > 0 ? parsed : 1;
}

function getUnlockedModules(bootcamp, lessonCount, nowMs) {
  const totalLessons = Number(lessonCount) || 0;
  if (totalLessons <= 0) return 0;
  const startMs = new Date(bootcamp.start_date).getTime();
  if (!Number.isFinite(startMs) || nowMs < startMs) return 0;
  const stutterDays = parseStutterDays(bootcamp.stutter_time);
  const elapsedDays = Math.floor((nowMs - startMs) / DAY_MS);
  const unlocked = Math.floor(elapsedDays / stutterDays) + 1;
  return Math.max(0, Math.min(totalLessons, unlocked));
}

function formatHumanDate(input) {
  const d = new Date(input);
  if (!Number.isFinite(d.getTime())) return '';
  const day = d.getDate();
  const suffix = (n) => {
    if (n % 100 >= 11 && n % 100 <= 13) return 'th';
    if (n % 10 === 1) return 'st';
    if (n % 10 === 2) return 'nd';
    if (n % 10 === 3) return 'rd';
    return 'th';
  };
  const month = d.toLocaleString('en-AU', { month: 'short' });
  const hours = d.getHours();
  const mins = String(d.getMinutes()).padStart(2, '0');
  const ampm = hours >= 12 ? 'pm' : 'am';
  const hour12 = hours % 12 || 12;
  return `${day}${suffix(day)} of ${month}, ${hour12}:${mins}${ampm}`;
}

async function getTopicActivityByClient(clientIds) {
  const ids = (clientIds || []).map(String).filter(Boolean);
  if (ids.length === 0) return {};

  const { data: teamsData } = await supabase
    .from('teams')
    .select('id, client_id, active')
    .in('client_id', ids)
    .eq('active', true);
  const teams = teamsData || [];
  if (teams.length === 0) return {};

  const teamIds = teams.map((t) => t.id);
  const teamToClient = new Map(teams.map((t) => [String(t.id), String(t.client_id)]));

  const { data: bootcampsData } = await supabase
    .from('bootcamps')
    .select('id, team_id, course_id, start_date, stutter_time')
    .in('team_id', teamIds);
  const bootcamps = (bootcampsData || []).filter((b) => b.course_id != null);
  if (bootcamps.length === 0) return {};

  const bootcampIds = bootcamps.map((b) => b.id);
  const courseIds = Array.from(new Set(bootcamps.map((b) => String(b.course_id))));
  const bootcampMeta = new Map(
    bootcamps.map((b) => {
      const teamKey = String(b.team_id);
      const clientId = teamToClient.get(teamKey);
      return [String(b.id), { clientId, teamId: teamKey, courseId: String(b.course_id) }];
    })
  );

  const { data: coursesData } = await supabase
    .from('courses')
    .select('id, name')
    .in('id', courseIds);
  const courseNameById = new Map((coursesData || []).map((c) => [String(c.id), c.name || `Course ${c.id}`]));

  const { data: lessonsData } = await supabase
    .from('lessons')
    .select('lesson_id, course_id, title, position, lesson_type')
    .in('course_id', courseIds);
  const lessons = (lessonsData || []).slice().sort((a, b) => {
    const pa = Number.isFinite(a.position) ? a.position : 9999;
    const pb = Number.isFinite(b.position) ? b.position : 9999;
    if (pa !== pb) return pa - pb;
    return Number(a.lesson_id) - Number(b.lesson_id);
  });
  const lessonIds = lessons.map((l) => l.lesson_id);
  const lessonsByCourse = {};
  const orderedLessonsByCourse = {};
  lessons.forEach((l) => {
    const key = String(l.course_id);
    if (!lessonsByCourse[key]) lessonsByCourse[key] = [];
    lessonsByCourse[key].push(l);
  });
  Object.keys(lessonsByCourse).forEach((courseId) => {
    const allLessons = lessonsByCourse[courseId] || [];
    const nonOnboarding = allLessons.filter((l) => l.lesson_type !== 'onboarding');
    const ordered = (nonOnboarding.length > 0 ? nonOnboarding : allLessons).slice().sort((a, b) => {
      const pa = Number.isFinite(a.position) ? a.position : 9999;
      const pb = Number.isFinite(b.position) ? b.position : 9999;
      if (pa !== pb) return pa - pb;
      return Number(a.lesson_id) - Number(b.lesson_id);
    });
    orderedLessonsByCourse[courseId] = ordered;
  });

  const { data: teamUsersData } = await supabase
    .from('team_users')
    .select('team_id, user_id')
    .in('team_id', teamIds);
  const usersByTeam = new Map();
  (teamUsersData || []).forEach((tu) => {
    const teamKey = String(tu.team_id);
    if (!usersByTeam.has(teamKey)) usersByTeam.set(teamKey, new Set());
    usersByTeam.get(teamKey).add(String(tu.user_id));
  });

  const completionCountByClientCourseLesson = new Map();
  const incrementCount = (map, key) => {
    map.set(key, (map.get(key) || 0) + 1);
  };

  const completionsData = await fetchLessonCompletionsForBootcamps(bootcampIds);
  completionsData.forEach((row) => {
    const meta = bootcampMeta.get(String(row.bootcamp_id));
    if (!meta?.clientId) return;
    const combinedKey = `${meta.clientId}|${meta.courseId}|${row.lesson_id}`;
    incrementCount(completionCountByClientCourseLesson, combinedKey);
  });

  const { data: questionsData } = await supabase
    .from('questions')
    .select('id, lesson_id, question_type')
    .in('lesson_id', lessonIds)
    .in('question_type', ['reflection']);
  const reflectionQuestionCountByLesson = new Map();
  const questionToLesson = new Map();
  (questionsData || []).forEach((q) => {
    const lessonKey = String(q.lesson_id);
    reflectionQuestionCountByLesson.set(lessonKey, (reflectionQuestionCountByLesson.get(lessonKey) || 0) + 1);
    questionToLesson.set(String(q.id), lessonKey);
  });

  const clientCourseUsers = new Map(); // key: clientId|courseId -> Set(userId)
  bootcamps.forEach((b) => {
    const meta = bootcampMeta.get(String(b.id));
    if (!meta?.clientId) return;
    const key = `${meta.clientId}|${meta.courseId}`;
    if (!clientCourseUsers.has(key)) clientCourseUsers.set(key, new Set());
    const teamUsers = usersByTeam.get(meta.teamId);
    if (!teamUsers) return;
    teamUsers.forEach((u) => clientCourseUsers.get(key).add(u));
  });

  const reflectionCountByClientCourseLesson = new Map();
  const { data: reflectionsData } = await supabase
    .from('user_reflections')
    .select('bootcamp_id, reflection_question_id')
    .in('bootcamp_id', bootcampIds);
  (reflectionsData || []).forEach((rf) => {
    const meta = bootcampMeta.get(String(rf.bootcamp_id));
    if (!meta?.clientId) return;
    const lessonKey = questionToLesson.get(String(rf.reflection_question_id));
    if (!lessonKey) return;
    const key = `${meta.clientId}|${meta.courseId}|${lessonKey}`;
    reflectionCountByClientCourseLesson.set(key, (reflectionCountByClientCourseLesson.get(key) || 0) + 1);
  });

  const byClient = {};
  const clientCourseSet = new Set(
    bootcamps
      .map((b) => {
        const meta = bootcampMeta.get(String(b.id));
        return meta?.clientId ? `${meta.clientId}|${meta.courseId}` : null;
      })
      .filter(Boolean)
  );

  clientCourseSet.forEach((clientCourse) => {
    const [clientId, courseId] = clientCourse.split('|');
    const lessonList = orderedLessonsByCourse[courseId] || [];
    const userCount = (clientCourseUsers.get(clientCourse) || new Set()).size;

    const lessonRows = lessonList
      .map((l) => {
        const lessonKey = String(l.lesson_id);
        const combinedKey = `${clientId}|${courseId}|${lessonKey}`;
        const reflections = reflectionCountByClientCourseLesson.get(combinedKey) || 0;
        const questionCount = reflectionQuestionCountByLesson.get(lessonKey) || 0;
        const denom = userCount * questionCount;
        const avg = denom > 0 ? reflections / denom : 0;
        const checkpointsCompleted = completionCountByClientCourseLesson.get(combinedKey) || 0;
        return {
          lessonId: l.lesson_id,
          lessonTitle: l.title || `Lesson ${l.lesson_id}`,
          reflections,
          questionCount,
          avgActivity: avg,
          checkpointsCompleted,
        };
      })
      .filter((row) => row.reflections > 0 || row.checkpointsCompleted > 0);

    const maxAvg = Math.max(0, ...lessonRows.map((r) => r.avgActivity || 0));
    const normalizedRows = lessonRows.map((r) => ({
      ...r,
      relativeActivity: maxAvg > 0 ? Math.max(0, Math.min(1, r.avgActivity / maxAvg)) : 0,
    }));
    if (normalizedRows.length === 0) return;

    if (!byClient[clientId]) byClient[clientId] = [];
    byClient[clientId].push({
      courseId,
      courseName: courseNameById.get(courseId) || `Course ${courseId}`,
      lessons: normalizedRows,
    });
  });

  Object.keys(byClient).forEach((clientId) => {
    byClient[clientId].sort((a, b) => String(a.courseName).localeCompare(String(b.courseName)));
  });

  return byClient;
}

async function getRegionActivityByClient(clientIds) {
  const ids = (clientIds || []).map(String).filter(Boolean);
  if (ids.length === 0) return { regionsByClient: {}, latestByClient: {} };

  const { data: serverTimeData } = await supabase.rpc('get_server_time');
  const nowMs = new Date(serverTimeData || Date.now()).getTime();

  const { data: regionsData } = await supabase
    .from('regions')
    .select('id, client_id, name, active')
    .in('client_id', ids)
    .eq('active', true);

  const regions = regionsData || [];
  const regionIds = regions.map((r) => r.id);
  if (regionIds.length === 0) return { regionsByClient: {}, latestByClient: {} };

  const { data: teamsData } = await supabase
    .from('teams')
    .select('id, region_id, active, client_id')
    .in('region_id', regionIds)
    .in('client_id', ids)
    .eq('active', true);

  const teams = teamsData || [];
  const teamIds = teams.map((t) => t.id);
  const teamNameById = new Map(teams.map((t) => [String(t.id), t.name || `Team ${t.id}`]));
  const teamToRegion = new Map(teams.map((t) => [String(t.id), String(t.region_id)]));
  const regionToClient = new Map(regions.map((r) => [String(r.id), String(r.client_id)]));

  const usersByTeam = new Map();
  if (teamIds.length > 0) {
    const { data: teamUsersData } = await supabase
      .from('team_users')
      .select('team_id, user_id')
      .in('team_id', teamIds);
    (teamUsersData || []).forEach((row) => {
      const key = String(row.team_id);
      if (!usersByTeam.has(key)) usersByTeam.set(key, new Set());
      usersByTeam.get(key).add(String(row.user_id));
    });
  }

  let bootcamps = [];
  let bootcampIds = [];
  const pointsByBootcamp = new Map();
  const lessonsByCourse = new Map();
  const reflectionsByBootcamp = new Map();
  const latestBootcampIdByTeam = new Map();
  if (teamIds.length > 0) {
    const { data: bootcampsData } = await supabase
      .from('bootcamps')
      .select('id, team_id, course_id, start_date, stutter_time')
      .in('team_id', teamIds);
    bootcamps = bootcampsData || [];
    bootcampIds = bootcamps.map((b) => b.id);
    (bootcamps || []).forEach((bootcamp) => {
      const teamKey = String(bootcamp.team_id);
      const cur = latestBootcampIdByTeam.get(teamKey);
      if (!cur) {
        latestBootcampIdByTeam.set(teamKey, {
          id: bootcamp.id,
          startTs: new Date(bootcamp.start_date).getTime(),
        });
        return;
      }
      const nextTs = new Date(bootcamp.start_date).getTime();
      if (nextTs >= cur.startTs) {
        latestBootcampIdByTeam.set(teamKey, { id: bootcamp.id, startTs: nextTs });
      }
    });
    if (bootcampIds.length > 0) {
      const { data: pointsData } = await supabase
        .from('user_points')
        .select('bootcamp_id, points')
        .in('bootcamp_id', bootcampIds);
      (pointsData || []).forEach((row) => {
        const key = String(row.bootcamp_id);
        pointsByBootcamp.set(key, (pointsByBootcamp.get(key) || 0) + (Number(row.points) || 0));
      });

      const { data: reflectionsData } = await supabase
        .from('user_reflections')
        .select('id, bootcamp_id, user_id, reflection_text, follow_up_question, follow_up_answer, submitted_at')
        .in('bootcamp_id', bootcampIds)
        .order('submitted_at', { ascending: false });
      const reflectionUserIds = Array.from(new Set((reflectionsData || []).map((r) => String(r.user_id)).filter(Boolean)));
      const userNameById = new Map();
      if (reflectionUserIds.length > 0) {
        const { data: usersData } = await supabase
          .from('users')
          .select('id, full_name, email')
          .in('id', reflectionUserIds);
        (usersData || []).forEach((u) => {
          userNameById.set(String(u.id), u.full_name || u.email || 'Unknown user');
        });
      }
      const bootcampTeamIdById = new Map(bootcamps.map((b) => [String(b.id), String(b.team_id)]));
      (reflectionsData || []).forEach((row) => {
        const key = String(row.bootcamp_id);
        if (!reflectionsByBootcamp.has(key)) reflectionsByBootcamp.set(key, []);
        const teamId = bootcampTeamIdById.get(key);
        reflectionsByBootcamp.get(key).push({
          ...row,
          authorName: userNameById.get(String(row.user_id)) || 'Unknown user',
          teamName: teamNameById.get(teamId) || 'Unknown team',
        });
      });
    }
  }

  const lessonCountByCourse = new Map();
  const lessonIndexByCourseLesson = new Map();
  const orderedLessonsByCourse = {};
  if (bootcamps.length > 0) {
    const courseIds = Array.from(new Set(bootcamps.map((b) => b.course_id).filter((v) => v != null)));
    if (courseIds.length > 0) {
      const { data: lessonsData } = await supabase
        .from('lessons')
        .select('lesson_id, course_id, position, lesson_type')
        .in('course_id', courseIds);
      const lessonsByCourseRaw = {};
      (lessonsData || []).forEach((lesson) => {
        const key = String(lesson.course_id);
        if (!lessonsByCourseRaw[key]) lessonsByCourseRaw[key] = [];
        lessonsByCourseRaw[key].push(lesson);
      });
      courseIds.forEach((courseId) => {
        const key = String(courseId);
        const allLessons = lessonsByCourseRaw[key] || [];
        const nonOnboarding = allLessons.filter((l) => l.lesson_type !== 'onboarding');
        const ordered = (nonOnboarding.length > 0 ? nonOnboarding : allLessons).slice().sort((a, b) => {
          const pa = Number.isFinite(a.position) ? a.position : 9999;
          const pb = Number.isFinite(b.position) ? b.position : 9999;
          if (pa !== pb) return pa - pb;
          return Number(a.lesson_id) - Number(b.lesson_id);
        });
        orderedLessonsByCourse[key] = ordered;
        lessonCountByCourse.set(key, ordered.length);
        lessonsByCourse.set(key, ordered.length);
        ordered.forEach((lesson, index) => {
          lessonIndexByCourseLesson.set(`${key}|${lesson.lesson_id}`, index + 1);
        });
      });
    }
  }

  const clientLatestReflections = {};
  const regionNumerator = new Map();
  const regionDenominator = new Map();
  const regionCompletionCount = new Map();
  const regionTotalReflections = new Map();

  regions.forEach((r) => {
    const key = String(r.id);
    regionNumerator.set(key, 0);
    regionDenominator.set(key, 0);
    regionTotalReflections.set(key, 0);
  });

  bootcamps.forEach((bootcamp) => {
    const teamKey = String(bootcamp.team_id);
    const regionKey = teamToRegion.get(teamKey);
    if (!regionKey) return;

    const teamUsers = usersByTeam.get(teamKey) || new Set();
    const users = teamUsers.size;
    const courseId = String(bootcamp.course_id);
    const totalLessons = lessonCountByCourse.get(courseId) || lessonsByCourse.get(courseId) || 0;
    const unlocked = getUnlockedModules(bootcamp, totalLessons, nowMs);
    const latestForTeam = latestBootcampIdByTeam.get(teamKey);
    if (latestForTeam && String(latestForTeam.id) === String(bootcamp.id)) {
      const numeratorAdd = pointsByBootcamp.get(String(bootcamp.id)) || 0;
      const denominatorAdd = users * unlocked;
      regionNumerator.set(regionKey, (regionNumerator.get(regionKey) || 0) + numeratorAdd);
      regionDenominator.set(regionKey, (regionDenominator.get(regionKey) || 0) + denominatorAdd);
    }

    const reflections = reflectionsByBootcamp.get(String(bootcamp.id)) || [];
    regionTotalReflections.set(regionKey, (regionTotalReflections.get(regionKey) || 0) + reflections.length);
    if (reflections.length > 0) {
      const clientId = regionToClient.get(regionKey);
      if (clientId) {
        const merged = [...(clientLatestReflections[clientId] || []), ...reflections]
          .sort((a, b) => new Date(b.submitted_at).getTime() - new Date(a.submitted_at).getTime())
          .slice(0, 3);
        clientLatestReflections[clientId] = merged;
      }
    }
  });

  if (bootcampIds.length > 0) {
    const completionsData = await fetchLessonCompletionsForBootcamps(bootcampIds);
    const bootcampTeamById = new Map(bootcamps.map((b) => [String(b.id), String(b.team_id)]));
    completionsData.forEach((row) => {
      const teamKey = bootcampTeamById.get(String(row.bootcamp_id));
      if (!teamKey) return;
      const regionKey = teamToRegion.get(teamKey);
      if (!regionKey) return;
      regionCompletionCount.set(regionKey, (regionCompletionCount.get(regionKey) || 0) + 1);
    });
  }

  const byClient = {};
  regions.forEach((r) => {
    const clientId = String(r.client_id);
    if (!byClient[clientId]) byClient[clientId] = [];
    const regionKey = String(r.id);
    const numerator = regionNumerator.get(regionKey) || 0;
    const denominator = regionDenominator.get(regionKey) || 0;
    const rawScore = denominator > 0 ? numerator / denominator : 0;
    const checkpointsCompleted = regionCompletionCount.get(regionKey) || 0;
    byClient[clientId].push({
      regionId: r.id,
      regionName: r.name || `Region ${r.id}`,
      rawScore,
      checkpointsCompleted,
      totalReflections: regionTotalReflections.get(regionKey) || 0,
    });
  });

  Object.keys(byClient).forEach((clientId) => {
    const maxRaw = Math.max(0, ...byClient[clientId].map((row) => row.rawScore || 0));
    byClient[clientId] = byClient[clientId]
      .map((row) => ({
        ...row,
        relativeScore: maxRaw > 0 ? Math.max(0, Math.min(1, row.rawScore / maxRaw)) : 0,
      }))
      .sort((a, b) => {
        if (b.relativeScore !== a.relativeScore) return b.relativeScore - a.relativeScore;
        return String(a.regionName).localeCompare(String(b.regionName));
      });
  });

  return { regionsByClient: byClient, latestByClient: clientLatestReflections };
}

async function getAccessibleClientsForUser(userId) {
  const [{ data: cliRows }, { data: regRows }] = await Promise.all([
    supabase.from('admin_user_clients').select('client_id').eq('user_id', userId),
    supabase.from('admin_user_regions').select('region_id').eq('user_id', userId),
  ]);

  const clientIds = new Set((cliRows || []).map((r) => String(r.client_id)));
  const clientScopeIds = new Set((cliRows || []).map((r) => String(r.client_id)));
  const regionIds = (regRows || []).map((r) => r.region_id);
  if (regionIds.length > 0) {
    const { data: regionMap } = await supabase
      .from('regions')
      .select('id, client_id')
      .in('id', regionIds);
    (regionMap || []).forEach((r) => clientIds.add(String(r.client_id)));
  }

  const ids = Array.from(clientIds).filter(Boolean);
  if (ids.length === 0) return [];

  const { data: clients } = await supabase
    .from('clients')
    .select('id, business_name')
    .in('id', ids);

  return (clients || [])
    .map((c) => ({ ...c, hasClientScope: clientScopeIds.has(String(c.id)) }))
    .slice()
    .sort((a, b) => String(a.business_name || '').localeCompare(String(b.business_name || '')));
}

function ManagerHome() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [analyticsByClient, setAnalyticsByClient] = useState({});
  const [latestByClient, setLatestByClient] = useState({});
  const [topicByClient, setTopicByClient] = useState({});
  const [hoveredRegionRowKey, setHoveredRegionRowKey] = useState('');
  const [isGlobalUser, setIsGlobalUser] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    const run = async () => {
      setLoading(true);
      setError('');
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const userId = sessionData?.session?.user?.id;
        if (!userId) {
          setError('Unauthorized');
          setLoading(false);
          return;
        }
        const { data: globalRow } = await supabase
          .from('lean_admin_users')
          .select('is_global')
          .eq('user_id', userId)
          .eq('is_global', true)
          .maybeSingle();
        if (!mounted) return;
        setIsGlobalUser(!!globalRow);
        const cacheKey = `manager.overview.${userId}.${getAdminViewMode()}.v1`;
        try {
          const raw = localStorage.getItem(cacheKey);
          if (raw) {
            const parsed = JSON.parse(raw);
            if ((Date.now() - (parsed?.savedAt || 0)) < OVERVIEW_CACHE_TTL_MS && parsed?.data) {
              if (!mounted) return;
              setClients(parsed.data.clients || []);
              setAnalyticsByClient(parsed.data.analyticsByClient || {});
              setLatestByClient(parsed.data.latestByClient || {});
              setTopicByClient(parsed.data.topicByClient || {});
              setLoading(false);
              return;
            }
          }
        } catch {}
        const list = await getAccessibleClientsForUser(userId);
        if (!mounted) return;
        setClients(list);
        const clientScopedIds = list.filter((c) => c.hasClientScope).map((c) => c.id);
        const [analytics, topicActivity] = await Promise.all([
          getRegionActivityByClient(clientScopedIds),
          getTopicActivityByClient(clientScopedIds),
        ]);
        if (!mounted) return;
        setAnalyticsByClient(analytics.regionsByClient || {});
        setLatestByClient(analytics.latestByClient || {});
        setTopicByClient(topicActivity || {});
        try {
          localStorage.setItem(
            cacheKey,
            JSON.stringify({
              savedAt: Date.now(),
              data: {
                clients: list,
                analyticsByClient: analytics.regionsByClient || {},
                latestByClient: analytics.latestByClient || {},
                topicByClient: topicActivity || {},
              },
            })
          );
        } catch {}
      } catch (e) {
        if (!mounted) return;
        setError('Failed to load your clients');
      } finally {
        if (!mounted) return;
        setLoading(false);
      }
    };
    run();
    return () => { mounted = false; };
  }, []);

  return (
    <div className="modern-container-narrow">
      <RenderMonitor componentName="ManagerHome" />
      <div className="modern-card">
        <div className="modern-flex modern-justify-between modern-items-center">
          <h1 className="modern-title">Your Clients</h1>
          <div className="modern-flex modern-items-center" style={{ gap: 10 }}>
            {isGlobalUser && (
              <button
                className="modern-button modern-button-info"
                onClick={() => {
                  setAdminViewMode('global');
                  navigate('/admin');
                }}
              >
                Switch to Global View
              </button>
            )}
            <button
              className="modern-button modern-button-secondary"
              onClick={async () => { try { await supabase.auth.signOut(); } catch {} navigate('/login', { replace: true }); }}
            >
              Logout
            </button>
          </div>
        </div>

        {loading ? (
          <div className="modern-loading">Loading…</div>
        ) : error ? (
          <div className="modern-error">{error}</div>
        ) : clients.length === 0 ? (
          <div style={{ color: '#64748b' }}>No accessible clients found for your account.</div>
        ) : (
          <div className="modern-flex modern-flex-col modern-gap-4" style={{ marginTop: 14 }}>
            {clients.map((c) => (
              <div key={c.id} className="modern-card" style={{ padding: 18 }}>
                <div className="modern-flex modern-justify-between modern-items-center">
                  <div style={{ fontWeight: 700 }}>{c.business_name || `Client ${c.id}`}</div>
                  {!c.hasClientScope && (
                    <button
                      className="modern-button modern-button-info"
                      onClick={() => navigate(`/client/${c.id}/view`, { state: { clientName: c.business_name || '' } })}
                    >
                      Per Region Data →
                    </button>
                  )}
                </div>
                {c.hasClientScope && (
                  <div style={{ marginTop: 12 }}>
                    <div style={{ color: '#475569', fontSize: 14, lineHeight: 1.5, marginBottom: 4 }}>
                      Below are the relative activity levels for different regions and topics. These activity levels are calculated by measuring the <strong>average amount of activity per user</strong> in each region/topic, and indexing the activity level against the most active region/topic.
                    </div>
                    <div style={{ color: '#475569', fontSize: 14, lineHeight: 1.5, marginBottom: 14 }}>
                      Click on a region to get more in-depth data.
                    </div>
                    <div style={{ fontWeight: 600, color: '#334155', marginBottom: 6 }}>Activity Levels per Region</div>
                    {(analyticsByClient[String(c.id)] || []).length === 0 ? (
                      <div style={{ color: '#94a3b8' }}>No active regions yet.</div>
                    ) : (
                      <div className="modern-flex modern-flex-col modern-gap-2">
                        {(analyticsByClient[String(c.id)] || []).map((row) => {
                          const rowKey = `${c.id}-${row.regionId}`;
                          return (
                          <div
                            key={rowKey}
                            onClick={() => navigate(`/client/${c.id}/view`, {
                              state: {
                                clientName: c.business_name || '',
                                targetRegionId: row.regionId,
                              },
                            })}
                            onMouseEnter={() => setHoveredRegionRowKey(rowKey)}
                            onMouseLeave={() => setHoveredRegionRowKey('')}
                            style={{
                              cursor: 'pointer',
                              borderRadius: 8,
                              padding: '4px 6px',
                              margin: '-4px -6px',
                              backgroundColor: hoveredRegionRowKey === rowKey ? '#f8fafc' : 'transparent',
                              transition: 'background-color 120ms ease',
                            }}
                          >
                            <div className="modern-flex modern-justify-between modern-items-center" style={{ flexWrap: 'nowrap' }}>
                              <span className="modern-flex modern-items-center" style={{ color: '#475569', gap: 8, whiteSpace: 'nowrap' }}>
                                <span
                                  style={{
                                    width: 12,
                                    height: 12,
                                    backgroundColor: '#FB7820',
                                    opacity: Math.max(0.12, row.relativeScore || 0),
                                    borderRadius: 2,
                                    display: 'inline-block',
                                  }}
                                />
                                {row.regionName}
                                <span style={{ color: '#64748b', fontSize: 13 }}>{row.totalReflections} reflections</span>
                                <span style={{ color: '#64748b', fontSize: 13 }}>{row.unlockedModules}/{row.totalModules} modules unlocked</span>
                              </span>
                              <span style={{ fontWeight: 600 }}>{(row.relativeScore || 0).toFixed(2)}</span>
                            </div>
                          </div>
                        )})}
                      </div>
                    )}
                    <div style={{ marginTop: 12 }}>
                      <div style={{ fontWeight: 600, color: '#334155', marginBottom: 6 }}>Activity Level by Topic</div>
                      {(topicByClient[String(c.id)] || []).length === 0 ? (
                        <div style={{ color: '#94a3b8', fontSize: 13 }}>No course topic activity found.</div>
                      ) : (
                        <div className="modern-flex modern-flex-col modern-gap-2">
                          {(topicByClient[String(c.id)] || []).flatMap((course) =>
                            (course.lessons || []).map((lesson) => (
                              <div key={`${course.courseId}-lesson-${lesson.lessonId}`}>
                                <div className="modern-flex modern-justify-between modern-items-center" style={{ flexWrap: 'nowrap' }}>
                                  <span className="modern-flex modern-items-center" style={{ color: '#475569', gap: 8, whiteSpace: 'nowrap' }}>
                                    <span
                                      title={`Relative activity level: ${(lesson.relativeActivity || 0).toFixed(2)}`}
                                      style={{
                                        width: 12,
                                        height: 12,
                                        backgroundColor: '#6E9277',
                                        opacity: Math.max(0.12, lesson.relativeActivity || 0),
                                        borderRadius: 2,
                                        display: 'inline-block',
                                      }}
                                    />
                                    {course.courseName} · {lesson.lessonTitle}
                                    <span style={{ color: '#64748b', fontSize: 13 }}>{lesson.reflections} reflections</span>
                                  </span>
                                  <span style={{ fontWeight: 600 }}>{(lesson.relativeActivity || 0).toFixed(2)}</span>
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                    <div style={{ marginTop: 10 }}>
                      <div style={{ fontWeight: 600, color: '#334155', marginBottom: 4 }}>Latest reflections</div>
                      {(latestByClient[String(c.id)] || []).length === 0 ? (
                        <div style={{ color: '#94a3b8', fontSize: 13 }}>No reflections yet.</div>
                      ) : (
                        <div className="modern-flex modern-flex-col modern-gap-1">
                          {(latestByClient[String(c.id)] || []).map((rf) => (
                            <div
                              key={`${c.id}-rf-${rf.id}`}
                              style={{ color: '#475569', fontSize: 13, padding: '8px 10px', borderRadius: 8, background: '#f8fafc', marginBottom: 6 }}
                            >
                              <div style={{ fontWeight: 700, marginBottom: 4 }}>
                                {rf.authorName || 'Unknown user'} - {rf.teamName || 'Unknown team'} - {formatHumanDate(rf.submitted_at)}
                              </div>
                              <ReflectionTextContent reflection={rf} />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export { getTopicActivityByClient, getRegionActivityByClient, formatHumanDate };

export default ManagerHome;




